/**
 * Candle level-of-detail tiers (Vela candle-lod.ts port).
 * Keyed by bar spacing (px between bar centers).
 *
 * - full:      high-low wick + body
 * - wick:      high-low stick only (spacing < CANDLE_BODY_MIN_SPACING)
 * - aggregate: bars sharing a pixel column collapse to high-low sticks
 *              (spacing < CANDLE_AGG_MAX_SPACING) — draw cost ~ screen width
 */

export const CANDLE_BODY_MIN_SPACING = 3   // below → wick-only
export const CANDLE_AGG_MAX_SPACING = 1    // below → aggregate per pixel column
export const CANDLE_WICK_W = 1.5

export function candleTier(spacing) {
  if (spacing < CANDLE_AGG_MAX_SPACING) return 'aggregate'
  if (spacing < CANDLE_BODY_MIN_SPACING) return 'wick'
  return 'full'
}

/** Snap CSS-px Y to the device-pixel grid (crisp body/wick edges). */
export function snapY(yCss, dpr) {
  return Math.round(yCss * dpr) / dpr
}

export function wickWidth(spacing) {
  const halfBody = Math.max(0.5, Math.floor(spacing * 0.7) / 2)
  return Math.max(1, Math.min(CANDLE_WICK_W, halfBody))
}

/**
 * Read OHLC fields from a SolCheddar candle (mcap) or plain OHLCV bar.
 */
function barOHLC(b) {
  const high = b.high_mcap ?? b.high
  const low = b.low_mcap ?? b.low
  const open = b.open_mcap ?? b.open
  const close = b.close_mcap ?? b.close
  const time = b.startTime ?? b.time ?? 0
  return { high, low, open, close, time }
}

/**
 * Aggregate bars whose x centers round to the same pixel column into high-low
 * sticks. Coverage is the UNION of true high-low ranges as contiguous runs
 * (price gaps stay voids). Sub-pixel voids (via yOf) coalesce.
 *
 * Returns [{ x, hi, lo, open, close, headTime }]
 */
export function aggregateCandleColumns(bars, i0, i1, xOf, yOf) {
  const out = []
  let col = NaN
  let intervals = []

  const flush = () => {
    if (intervals.length === 0) return
    // Coalesce runs whose void is sub-pixel — it cannot render anyway.
    const merged = [intervals[0]]
    for (let k = 1; k < intervals.length; k += 1) {
      const prev = merged[merged.length - 1]
      const next = intervals[k]
      if (Math.abs(yOf(prev.hi) - yOf(next.lo)) < 1) {
        prev.hi = next.hi
        if (next.headIdx < prev.headIdx) {
          prev.headIdx = next.headIdx
          prev.headTime = next.headTime
          prev.open = next.open
        }
        if (next.lastIdx > prev.lastIdx) {
          prev.lastIdx = next.lastIdx
          prev.close = next.close
        }
      } else {
        merged.push(next)
      }
    }
    for (const iv of merged) {
      out.push({
        x: col,
        hi: iv.hi,
        lo: iv.lo,
        headTime: iv.headTime,
        open: iv.open,
        close: iv.close,
      })
    }
    intervals = []
  }

  for (let i = i0; i <= i1; i += 1) {
    const raw = bars[i]
    if (!raw) continue
    const b = barOHLC(raw)
    if (!(b.high > b.low)) continue
    const x = Math.round(xOf(i))
    if (x !== col) {
      flush()
      col = x
    }
    let lo = b.low
    let hi = b.high
    let headIdx = i
    let headTime = b.time
    let open = b.open
    for (let k = intervals.length - 1; k >= 0; k -= 1) {
      const iv = intervals[k]
      if (iv.lo > hi || iv.hi < lo) continue
      if (iv.lo < lo) lo = iv.lo
      if (iv.hi > hi) hi = iv.hi
      if (iv.headIdx < headIdx) {
        headIdx = iv.headIdx
        headTime = iv.headTime
        open = iv.open
      }
      intervals.splice(k, 1)
    }
    let insertAt = 0
    while (insertAt < intervals.length && intervals[insertAt].lo < lo) insertAt += 1
    intervals.splice(insertAt, 0, {
      lo, hi, headIdx, headTime, open, lastIdx: i, close: b.close,
    })
  }
  flush()
  return out
}
